// marketDataService.js
// Service to fetch market data from various APIs

// API Configuration
const API_CONFIG = {
  ALPHA_VANTAGE: {
    BASE_URL: 'https://www.alphavantage.co/query',
    API_KEY: process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || 'YOUR_ALPHA_VANTAGE_API_KEY'
  },
  COIN_GECKO: {
    BASE_URL: 'https://api.coingecko.com/api/v3',
    API_KEY: process.env.REACT_APP_COIN_GECKO_API_KEY || null
  }
};

// Helper function to handle API errors
const handleApiError = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorText}`);
  }
  return response;
};

// Fetch stock data from Alpha Vantage
export const fetchStockData = async (symbol) => {
  try {
    const response = await fetch(
      `${API_CONFIG.ALPHA_VANTAGE.BASE_URL}?function=GLOBAL_QUOTE&symbol=${symbol}&apikey=${API_CONFIG.ALPHA_VANTAGE.API_KEY}`
    );
    
    await handleApiError(response);
    const data = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }
    
    if (data['Note']) {
      throw new Error('API call frequency limit reached. Please try again later.');
    }
    
    const quote = data['Global Quote'];
    if (!quote) {
      throw new Error('No data available for this symbol');
    }
    
    return {
      symbol: quote['01. symbol'],
      price: parseFloat(quote['05. price']),
      change: parseFloat(quote['09. change']),
      changePercent: parseFloat(quote['10. change percent'].replace('%', ''))
    };
  } catch (error) {
    console.error(`Error fetching stock data for ${symbol}:`, error);
    throw error;
  }
};

// Fetch cryptocurrency data from CoinGecko
export const fetchCryptoData = async (ids) => {
  try {
    const response = await fetch(
      `${API_CONFIG.COIN_GECKO.BASE_URL}/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`
    );
    
    await handleApiError(response);
    const data = await response.json();
    
    return Object.keys(data).map(id => ({
      id,
      symbol: id.toUpperCase(),
      price: data[id].usd,
      changePercent: data[id].usd_24h_change || 0
    }));
  } catch (error) {
    console.error(`Error fetching crypto data for ${ids}:`, error);
    throw error;
  }
};

// Fetch market data for multiple symbols
export const fetchMarketData = async (symbols) => {
  try {
    const results = await Promise.allSettled(
      symbols.map(symbol => {
        // Determine if it's a cryptocurrency or stock based on symbol
        if (['BTC', 'ETH', 'SOL', 'ADA', 'XRP', 'DOT', 'DOGE', 'AVAX', 'MATIC', 'LINK'].includes(symbol)) {
          return fetchCryptoData(symbol.toLowerCase());
        } else {
          return fetchStockData(symbol);
        }
      })
    );
    
    return results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value)
      .flat();
  } catch (error) {
    console.error('Error fetching market data:', error);
    throw error;
  }
};

// Fetch top cryptocurrencies
export const fetchTopCryptocurrencies = async (limit = 10) => {
  try {
    const response = await fetch(
      `${API_CONFIG.COIN_GECKO.BASE_URL}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${limit}&page=1&sparkline=false&price_change_percentage=24h`
    );
    
    await handleApiError(response);
    const data = await response.json();
    
    return data.map(coin => ({
      id: coin.id,
      symbol: coin.symbol.toUpperCase(),
      name: coin.name,
      price: coin.current_price,
      changePercent: coin.price_change_percentage_24h || 0,
      image: coin.image
    }));
  } catch (error) {
    console.error('Error fetching top cryptocurrencies:', error);
    throw error;
  }
};

// Fetch top stocks (using a predefined list)
export const fetchTopStocks = async () => {
  const stockSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX', 'AMD', 'INTC'];
  
  try {
    const results = await Promise.allSettled(
      stockSymbols.map(symbol => fetchStockData(symbol))
    );
    
    return results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);
  } catch (error) {
    console.error('Error fetching top stocks:', error);
    throw error;
  }
};

// Fetch commodities data (using Alpha Vantage)
export const fetchCommoditiesData = async () => {
  // For commodities, we'll use a different approach since Alpha Vantage requires specific symbols
  // This is a simplified version - in a real implementation, you would use specific commodity symbols
  const commodities = [
    { symbol: 'XAU', name: 'Gold' },
    { symbol: 'XAG', name: 'Silver' }
  ];
  
  try {
    // Note: This is a simplified implementation. Real commodity data would require specific API calls
    // For demonstration purposes, we'll return static data
    return commodities.map(commodity => ({
      symbol: commodity.symbol,
      name: commodity.name,
      price: Math.random() * 1000 + 1000, // Random price for demonstration
      changePercent: (Math.random() - 0.5) * 10 // Random change percentage
    }));
  } catch (error) {
    console.error('Error fetching commodities data:', error);
    throw error;
  }
};

// Fetch currency exchange rates (using Alpha Vantage)
export const fetchCurrencyData = async () => {
  const currencies = ['EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF'];
  
  try {
    // Note: This is a simplified implementation. Real currency data would require specific API calls
    // For demonstration purposes, we'll return static data
    return currencies.map(currency => ({
      symbol: `${currency}/USD`,
      name: currency,
      price: Math.random() * 2, // Random price for demonstration
      changePercent: (Math.random() - 0.5) * 5 // Random change percentage
    }));
  } catch (error) {
    console.error('Error fetching currency data:', error);
    throw error;
  }
};

// Fetch indices data (using Alpha Vantage)
export const fetchIndicesData = async () => {
  const indices = ['SPX', 'DJI', 'IXIC', 'RUT', 'VIX'];
  
  try {
    // Note: This is a simplified implementation. Real indices data would require specific API calls
    // For demonstration purposes, we'll return static data
    return indices.map(index => ({
      symbol: index,
      name: index,
      price: Math.random() * 10000 + 1000, // Random price for demonstration
      changePercent: (Math.random() - 0.5) * 5 // Random change percentage
    }));
  } catch (error) {
    console.error('Error fetching indices data:', error);
    throw error;
  }
};

export default {
  fetchMarketData,
  fetchStockData,
  fetchCryptoData,
  fetchTopCryptocurrencies,
  fetchTopStocks,
  fetchCommoditiesData,
  fetchCurrencyData,
  fetchIndicesData
};